import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Trophy, User, Calendar, Film, Star, X, Plus, Heart, MessageCircle, Share2, Trash2, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatTempo, formatTimeAgo, getDiscordAvatarUrl, extractYouTubeId, getYouTubeThumbnail, validateYouTubeUrl } from '../lib/timeUtils';
import ConfirmModal from '../components/ConfirmModal';
import CreateStoryModal from '../components/CreateStoryModal';
import { 
  createPost, 
  searchMovies, 
  fetchUserPosts, 
  deletePost, 
  likePost, 
  fetchUserProfileById, 
  fetchPostComments,
  fetchUserVideos,
  createVideo,
  deleteVideo,
  fetchUserProfile,
  searchUsers
} from '../lib/api';

// Componente de carrossel de vídeos (reutilizável)
const VideoCarousel = ({ videos, videoType, onVideoClick, onAddVideo, isOwnProfile }) => {
  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 340;
      carouselRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!videos || videos.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        {isOwnProfile ? 'Você ainda não compartilhou vídeos aqui.' : 'Este usuário ainda não compartilhou vídeos aqui.'}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => scrollCarousel(-1)}
        className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-[45px] h-[45px] bg-purple-600/90 hover:bg-purple-600 border-none rounded-full text-white text-xl cursor-pointer transition-all z-10 flex items-center justify-center shadow-[0_4px_15px_rgba(139,92,246,0.4)] hover:scale-110"
      >
        ‹
      </button>
      <div 
        ref={carouselRef}
        className="flex gap-5 overflow-x-auto scroll-smooth scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {videos.map((video) => (
          <div
            key={video.id}
            onClick={() => onVideoClick(videoType, video.id)}
            className="bg-black/30 border border-purple-500/15 rounded-2xl overflow-hidden transition-all cursor-pointer min-w-[320px] flex-shrink-0 hover:border-purple-500/40 hover:-translate-y-[5px] hover:shadow-[0_10px_30px_rgba(139,92,246,0.3)]"
          >
            <div className="relative w-full h-[180px] bg-gradient-to-br from-purple-600/20 to-indigo-600/20 flex items-center justify-center overflow-hidden">
              <img 
                src={getYouTubeThumbnail(video.youtube_video_id)} 
                alt={video.title || 'Video'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/320x180?text=Video';
                }}
              />
              <div className="absolute w-[50px] h-[50px] bg-purple-600/90 hover:bg-purple-600 rounded-full flex items-center justify-center text-xl transition-all hover:scale-120">
                ▶
              </div>
            </div>
            <div className="p-4">
              <div className="text-sm font-semibold mb-2 text-white line-clamp-2">
                {video.title || 'Sem título'}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                <span>{videoType === 'shame-wall' ? '😂' : '🔥'}</span>
                <span>•</span>
                <span>{formatTimeAgo(video.created_at)}</span>
              </div>
              <div className="flex gap-3 pt-2.5 border-t border-purple-500/10">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">👁️ {video.likes_count || 0}</div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">{videoType === 'shame-wall' ? '😂' : '🔥'} {video.likes_count || 0}</div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">💬 {video.comments_count || 0}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={() => scrollCarousel(1)}
        className="absolute right-[-15px] top-1/2 -translate-y-1/2 w-[45px] h-[45px] bg-purple-600/90 hover:bg-purple-600 border-none rounded-full text-white text-xl cursor-pointer transition-all z-10 flex items-center justify-center shadow-[0_4px_15px_rgba(139,92,246,0.4)] hover:scale-110"
      >
        ›
      </button>
    </div>
  );
};

// Componente de busca de usuários para adicionar à lista
const UserSearchInput = ({ users, onAddUser, onRemoveUser, placeholder = "Buscar por nome ou Discord ID..." }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setSearching(true);
      try {
        const results = await searchUsers(searchQuery);
        setSearchResults(results || []);
        setShowResults(true);
      } catch (error) {
        console.error('Erro ao buscar usuários:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    // Verificar se o usuário já não está na lista
    const userExists = users.some(u => 
      (typeof u === 'string' ? u === user.discord_id : u.discord_id === user.discord_id)
    );
    
    if (!userExists) {
      onAddUser({
        id: user.id,
        discord_id: user.discord_id,
        username: user.discord_username,
        avatar: user.discord_avatar
      });
    }
    setSearchQuery('');
    setShowResults(false);
  };

  const handleAddByDiscordId = () => {
    if (searchQuery.trim()) {
      // Verificar se já não está na lista
      const exists = users.some(u => 
        (typeof u === 'string' ? u === searchQuery.trim() : u.discord_id === searchQuery.trim())
      );
      
      if (!exists) {
        // Adicionar como string (discord_id) para manter compatibilidade
        onAddUser(searchQuery.trim());
      }
      setSearchQuery('');
      setShowResults(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2 mb-2">
        <div className="flex-1 relative">
          <div className="relative flex items-center">
            <Search size={16} className="absolute left-3 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (searchResults.length > 0) {
                    handleSelectUser(searchResults[0]);
                  } else {
                    handleAddByDiscordId();
                  }
                }
              }}
              placeholder={placeholder}
              className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-2 pl-10 outline-none focus:border-purple-500/50 transition-colors text-white"
            />
          </div>
          
          {showResults && (searchResults.length > 0 || searching) && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-[#111827]/95 backdrop-blur-md border border-purple-500/20 rounded-[10px] max-h-60 overflow-y-auto z-50">
              {searching ? (
                <div className="p-4 text-center text-gray-400 text-sm">Buscando...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    className="w-full p-3 hover:bg-white/10 flex items-center gap-3 transition-colors border-b border-white/5 last:border-0"
                  >
                    <img
                      src={getDiscordAvatarUrl(user.discord_id, user.discord_avatar)}
                      alt={user.discord_username}
                      className="w-8 h-8 rounded-full border border-purple-500/40"
                    />
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-sm text-white">{user.discord_username}</p>
                      <p className="text-xs text-gray-400">{user.discord_id}</p>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-400 text-sm">
                  Nenhum usuário encontrado. Pressione Enter para adicionar por Discord ID.
                </div>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleAddByDiscordId}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-sm transition-colors flex items-center gap-2"
        >
          <Plus size={18} />
          Adicionar ID
        </button>
      </div>
    </div>
  );
};

// Modal para adicionar vídeo
const AddVideoModal = ({ isOpen, onClose, videoType, onVideoAdded }) => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!youtubeUrl.trim()) {
      setError('Por favor, informe a URL do YouTube');
      return;
    }

    if (!validateYouTubeUrl(youtubeUrl)) {
      setError('URL do YouTube inválida');
      return;
    }

    const youtubeVideoId = extractYouTubeId(youtubeUrl);
    if (!youtubeVideoId) {
      setError('Não foi possível extrair o ID do vídeo do YouTube');
      return;
    }

    setSubmitting(true);
    try {
      // Converter mentionedUsers para array de discord_ids se necessário
      const mentionedUserIds = mentionedUsers.map(u => typeof u === 'string' ? u : u.discord_id);
      
      const videoData = {
        youtube_url: youtubeUrl,
        youtube_video_id: youtubeVideoId,
        title: title.trim() || null,
        description: description.trim() || null,
        mentioned_users: mentionedUserIds
      };

      await createVideo(videoType, videoData);
      onVideoAdded();
      onClose();
      setYoutubeUrl('');
      setTitle('');
      setDescription('');
      setMentionedUsers([]);
    } catch (err) {
      console.error('Erro ao criar vídeo:', err);
      setError('Erro ao criar vídeo. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMentioned = (user) => {
    const userDiscordId = typeof user === 'string' ? user : user.discord_id;
    const exists = mentionedUsers.some(u => 
      (typeof u === 'string' ? u === userDiscordId : u.discord_id === userDiscordId)
    );
    
    if (!exists) {
      setMentionedUsers([...mentionedUsers, user]);
    }
  };

  const handleRemoveMentioned = (userToRemove) => {
    const userDiscordId = typeof userToRemove === 'string' ? userToRemove : userToRemove.discord_id;
    setMentionedUsers(mentionedUsers.filter(u => {
      const uDiscordId = typeof u === 'string' ? u : u.discord_id;
      return uDiscordId !== userDiscordId;
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-white/20 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-black flex items-center gap-2">
            Adicionar Vídeo - {videoType === 'shame-wall' ? '😱 Mural da Vergonha' : '⭐ Melhores Clipes'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-bold mb-2">URL do YouTube *</label>
            <input
              type="text"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 outline-none focus:border-purple-500/50 transition-colors text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Título (opcional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título do vídeo..."
              className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 outline-none focus:border-purple-500/50 transition-colors text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Descrição (opcional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição do vídeo..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 outline-none focus:border-purple-500/50 transition-colors resize-none text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Mencionar usuários</label>
            <UserSearchInput
              users={mentionedUsers}
              onAddUser={handleAddMentioned}
              onRemoveUser={handleRemoveMentioned}
              placeholder="Buscar por nome ou Discord ID..."
            />
            
            {mentionedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {mentionedUsers.map((user, idx) => {
                  const userDiscordId = typeof user === 'string' ? user : user.discord_id;
                  const username = typeof user === 'string' ? user : (user.username || userDiscordId);
                  const avatar = typeof user === 'string' ? null : user.avatar;
                  
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-1 bg-purple-600/20 border border-purple-600/50 rounded-sm text-sm"
                    >
                      {avatar && (
                        <img
                          src={getDiscordAvatarUrl(userDiscordId, avatar)}
                          alt={username}
                          className="w-5 h-5 rounded-full"
                        />
                      )}
                      <span>{username}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMentioned(user)}
                        className="text-gray-400 hover:text-white"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm font-bold transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || !youtubeUrl.trim()}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-sm font-bold hover:shadow-[0_0_20px_rgba(139,92,246,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Perfil = () => {
  const { user: currentUser } = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(true);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalType, setVideoModalType] = useState('shame-wall');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [userPosts, setUserPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [likingPosts, setLikingPosts] = useState(new Set());
  const [shameWallVideos, setShameWallVideos] = useState([]);
  const [bestClipsVideos, setBestClipsVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [showCreateStoryModal, setShowCreateStoryModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  const showModal = (config) => {
    setModalConfig({
      isOpen: true,
      type: config.type || 'info',
      title: config.title || '',
      message: config.message || '',
      onConfirm: config.onConfirm || null
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Verificar se deve abrir modal de vídeo
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const addVideoParam = urlParams.get('addVideo');
    if (addVideoParam && (addVideoParam === 'shame-wall' || addVideoParam === 'best-clips')) {
      setVideoModalType(addVideoParam);
      setShowVideoModal(true);
      // Limpar o parâmetro da URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Determinar qual usuário exibir
  useEffect(() => {
    if (userId) {
      setIsOwnProfile(false);
      const loadProfileUser = async () => {
        try {
          const profile = await fetchUserProfileById(userId);
          const profileData = {
            ...profile,
            total_seconds: profile.voice_stats?.total_seconds || profile.total_seconds || 0,
            weekly_seconds: profile.voice_stats?.weekly_seconds || profile.weekly_seconds || 0,
            monthly_seconds: profile.voice_stats?.monthly_seconds || profile.monthly_seconds || 0,
            movies_watched_count: profile.movies_watched_count || 0,
            movie_reviews_count: profile.movie_reviews_count || 0
          };
          setProfileUser(profileData);
        } catch (error) {
          console.error('Erro ao carregar perfil do usuário:', error);
          navigate('/feed');
        }
      };
      loadProfileUser();
    } else {
      setIsOwnProfile(true);
      if (currentUser) {
        const userData = {
          ...currentUser,
          total_seconds: currentUser.total_seconds || 0,
          weekly_seconds: currentUser.weekly_seconds || 0,
          monthly_seconds: currentUser.monthly_seconds || 0
        };
        setProfileUser(userData);
      }
    }
  }, [userId, currentUser?.id, navigate]);

  // Carregar vídeos do usuário
  useEffect(() => {
    const loadUserVideos = async () => {
      const targetUserId = userId || currentUser?.id;
      if (!targetUserId) return;
      
      try {
        setVideosLoading(true);
        const [shameWall, bestClips] = await Promise.all([
          fetchUserVideos(targetUserId, 'shame-wall'),
          fetchUserVideos(targetUserId, 'best-clips')
        ]);
        setShameWallVideos(shameWall || []);
        setBestClipsVideos(bestClips || []);
      } catch (error) {
        console.error('Erro ao carregar vídeos do usuário:', error);
        setShameWallVideos([]);
        setBestClipsVideos([]);
      } finally {
        setVideosLoading(false);
      }
    };

    loadUserVideos();
  }, [userId, currentUser?.id]);

  // Buscar posts do usuário
  useEffect(() => {
    const loadUserPosts = async () => {
      const targetUserId = userId || currentUser?.id;
      if (!targetUserId) return;
      
      try {
        setPostsLoading(true);
        const posts = await fetchUserPosts(targetUserId);
        const postsWithComments = posts || [];
        
        const postsWithCommentCounts = await Promise.all(
          postsWithComments.map(async (post) => {
            if (post.comments_count !== undefined) {
              return post;
            }
            try {
              const comments = await fetchPostComments(post.id);
              return {
                ...post,
                comments_count: comments?.length || 0
              };
            } catch (err) {
              console.error(`Erro ao buscar comentários do post ${post.id}:`, err);
              return {
                ...post,
                comments_count: 0
              };
            }
          })
        );
        
        setUserPosts(postsWithCommentCounts);
      } catch (error) {
        console.error('Erro ao carregar posts do usuário:', error);
        setUserPosts([]);
      } finally {
        setPostsLoading(false);
      }
    };

    loadUserPosts();
  }, [userId, profileUser?.id, currentUser?.id]);

  const user = profileUser || currentUser;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-white">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  const calculatePercentage = (value, max) => {
    if (!max || max === 0) return 0;
    const percentage = (value / max) * 100;
    return Math.min(percentage, 100);
  };

  const weeklyPercentage = calculatePercentage(user.weekly_seconds, user.total_seconds);
  const monthlyPercentage = calculatePercentage(user.monthly_seconds, user.total_seconds);

  const handleSearchMovies = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const results = await searchMovies(query);
      setSearchResults(results.results || []);
    } catch (error) {
      console.error('Erro ao buscar filmes:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectMovie = (movie) => {
    setSelectedMovie(movie);
    setSearchQuery(movie.title);
    setSearchResults([]);
  };

  const handleAddAttendee = (user) => {
    const userDiscordId = typeof user === 'string' ? user : user.discord_id;
    const exists = attendees.some(a => 
      (typeof a === 'string' ? a === userDiscordId : a.discord_id === userDiscordId)
    );
    
    if (!exists) {
      setAttendees([...attendees, user]);
    }
  };

  const handleRemoveAttendee = (attendeeToRemove) => {
    const attendeeDiscordId = typeof attendeeToRemove === 'string' ? attendeeToRemove : attendeeToRemove.discord_id;
    setAttendees(attendees.filter(a => {
      const aDiscordId = typeof a === 'string' ? a : a.discord_id;
      return aDiscordId !== attendeeDiscordId;
    }));
  };

  const handleClearForm = () => {
    setSelectedMovie(null);
    setRating(0);
    setReview('');
    setAttendees([]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    
    if (!selectedMovie) {
      showModal({
        type: 'warning',
        title: 'Atenção',
        message: 'Por favor, selecione um filme'
      });
      return;
    }

    if (!review.trim()) {
      showModal({
        type: 'warning',
        title: 'Atenção',
        message: 'Por favor, escreva uma avaliação'
      });
      return;
    }

    setSubmitting(true);
    try {
      const movieYear = selectedMovie.release_date 
        ? new Date(selectedMovie.release_date).getFullYear() 
        : selectedMovie.year;
      
      const moviePoster = selectedMovie.poster_path 
        ? (selectedMovie.poster_path.startsWith('http') 
            ? selectedMovie.poster_path 
            : `https://image.tmdb.org/t/p/w500${selectedMovie.poster_path}`)
        : null;

      const postData = {
        movie_id: selectedMovie.id.toString(),
        rating: rating > 0 ? rating : null,
        review: review.trim(),
        attendees: attendees,
        movie_title: selectedMovie.title,
        movie_year: movieYear,
        movie_poster: moviePoster
      };

      await createPost(postData);
      showModal({
        type: 'success',
        title: 'Sucesso',
        message: 'Avaliação publicada com sucesso!'
      });
      setShowPostModal(false);
      handleClearForm();
      
      const targetUserId = userId || currentUser?.id;
      if (targetUserId) {
        const posts = await fetchUserPosts(targetUserId);
        const postsWithComments = posts || [];
        
        const postsWithCommentCounts = await Promise.all(
          postsWithComments.map(async (post) => {
            if (post.comments_count !== undefined) {
              return post;
            }
            try {
              const comments = await fetchPostComments(post.id);
              return {
                ...post,
                comments_count: comments?.length || 0
              };
            } catch (err) {
              return {
                ...post,
                comments_count: 0
              };
            }
          })
        );
        
        setUserPosts(postsWithCommentCounts);
      }
    } catch (error) {
      console.error('Erro ao publicar avaliação:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao publicar avaliação. Tente novamente.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getUserAvatar = () => {
    return getDiscordAvatarUrl(user.discord_id, user.discord_avatar);
  };

  const handleLikePost = async (postId) => {
    if (likingPosts.has(postId)) return;
    
    try {
      setLikingPosts(prev => new Set(prev).add(postId));
      
      setUserPosts(prevPosts => prevPosts.map(post => {
        if (post.id === postId) {
          const newIsLiked = !post.is_liked;
          return {
            ...post,
            is_liked: newIsLiked,
            likes: newIsLiked ? (post.likes || 0) + 1 : Math.max((post.likes || 0) - 1, 0)
          };
        }
        return post;
      }));
      
      const response = await likePost(postId);
      
      setUserPosts(prevPosts => prevPosts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            is_liked: response.is_liked,
            likes: response.likes
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Erro ao curtir post:', error);
      setUserPosts(prevPosts => prevPosts.map(post => {
        if (post.id === postId) {
          const revertedIsLiked = !post.is_liked;
          return {
            ...post,
            is_liked: revertedIsLiked,
            likes: revertedIsLiked ? (post.likes || 0) + 1 : Math.max((post.likes || 0) - 1, 0)
          };
        }
        return post;
      }));
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao curtir post. Tente novamente.'
      });
    } finally {
      setLikingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  const handleDeletePost = async (postId) => {
    showModal({
      type: 'confirm',
      title: 'Confirmar exclusão',
      message: 'Tem certeza que deseja excluir esta publicação?',
      onConfirm: async () => {
        try {
          await deletePost(postId);
          const targetUserId = userId || currentUser?.id;
          if (targetUserId) {
            const posts = await fetchUserPosts(targetUserId);
            const postsWithComments = posts || [];
            
            const postsWithCommentCounts = await Promise.all(
              postsWithComments.map(async (post) => {
                if (post.comments_count !== undefined) {
                  return post;
                }
                try {
                  const comments = await fetchPostComments(post.id);
                  return {
                    ...post,
                    comments_count: comments?.length || 0
                  };
                } catch (err) {
                  return {
                    ...post,
                    comments_count: 0
                  };
                }
              })
            );
            
            setUserPosts(postsWithCommentCounts);
          }
        } catch (error) {
          console.error('Erro ao deletar post:', error);
          showModal({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao deletar publicação. Tente novamente.'
          });
        }
      }
    });
  };


  const handleVideoClick = (videoType, videoId) => {
    navigate(`/video/${videoType}/${videoId}`);
  };

  const handleVideoAdded = async () => {
    const targetUserId = userId || currentUser?.id;
    if (!targetUserId) return;
    
    try {
      const [shameWall, bestClips] = await Promise.all([
        fetchUserVideos(targetUserId, 'shame-wall'),
        fetchUserVideos(targetUserId, 'best-clips')
      ]);
      setShameWallVideos(shameWall || []);
      setBestClipsVideos(bestClips || []);
    } catch (error) {
      console.error('Erro ao recarregar vídeos:', error);
    }
  };

  return (
    <>
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
      
    <div className="min-h-screen bg-[#0a0a14] text-white relative">
      {/* Background gradient */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: `
          radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)
        `
      }}></div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#0a0a14]/95 backdrop-blur-xl border-b border-purple-500/20 px-8 py-4 flex justify-between items-center z-[1000]">
        <Link to="/feed" className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-all px-4 py-2 rounded-[10px] hover:bg-purple-600/10">
          <ArrowLeft size={18} />
          <span>Voltar ao Feed</span>
        </Link>
        <Link to="/feed" className="flex items-center gap-3">
          <img src="/oasis-logo.ico" alt="Oasis" className="w-10 h-10 rounded-[10px]" />
          <span className="text-2xl font-bold">OASIS</span>
        </Link>
        <div></div>
      </header>

      {/* Container */}
      <div className="max-w-[1200px] mx-auto px-5 pt-[100px] pb-10 relative z-10">
        
        {/* Profile Header */}
        <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-10 mb-8 backdrop-blur-md relative overflow-hidden animate-[slideIn_0.5s_ease-out]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-purple-600 via-indigo-600 via-pink-600 to-transparent"></div>
          
          <div className="flex gap-8 items-start mb-8">
            <div 
              className={`w-[150px] h-[150px] rounded-full border-4 border-purple-600 bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-6xl flex-shrink-0 shadow-[0_0_40px_rgba(139,92,246,0.6)] animate-[glow_3s_ease-in-out_infinite] ${
                isOwnProfile ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
              }`}
              onClick={() => {
                if (isOwnProfile) {
                  setShowCreateStoryModal(true);
                }
              }}
              title={isOwnProfile ? 'Clique para criar uma story' : ''}
            >
              {user?.discord_avatar ? (
                <img src={getUserAvatar()} alt="avatar" className="w-full h-full rounded-full" />
              ) : (
                '👤'
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-2.5">
                <h1 className="text-[32px] font-bold text-white">{user.discord_username}</h1>
                <div className="flex gap-2">
                  <span className="px-3 py-1.5 bg-purple-600/15 border border-purple-600/30 rounded-[20px] text-xs text-purple-400 flex items-center gap-1">🎬 Cinéfilo</span>
                  <span className="px-3 py-1.5 bg-purple-600/15 border border-purple-600/30 rounded-[20px] text-xs text-purple-400 flex items-center gap-1">⭐ Avaliador</span>
                </div>
              </div>
              {user.nickname && (
                <div className="text-base text-gray-500 mb-4">"{user.nickname}"</div>
              )}
              <p className="text-sm text-gray-400 leading-relaxed mb-5 max-w-[600px]">
                Apaixonado por cinema e séries. No Oásis, compartilho minhas experiências e descubro novos mundos através da tela.
              </p>
              {isOwnProfile && (
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPostModal(true)}
                    className="bg-gradient-to-br from-purple-600 to-indigo-600 border-none px-8 py-3 rounded-xl text-white font-semibold cursor-pointer transition-all text-sm shadow-[0_4px_15px_rgba(139,92,246,0.4)] hover:-translate-y-[2px] hover:shadow-[0_8px_25px_rgba(139,92,246,0.6)]"
                  >
                    📝 Publicar Avaliação
                  </button>
                  <button className="bg-purple-600/10 border border-purple-600/30 px-8 py-3 rounded-xl text-purple-400 font-semibold cursor-pointer transition-all text-sm hover:bg-purple-600/20 hover:-translate-y-[2px]">
                    ⚙️ Editar Perfil
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-5">
            <div className="bg-black/30 border border-purple-500/20 rounded-2xl p-5 text-center transition-all hover:border-purple-500/40 hover:-translate-y-[5px]">
              <div className="text-3xl mb-2.5">⏱️</div>
              <div className="text-[28px] font-bold text-purple-600 mb-1">{formatTempo(user.total_seconds || 0)}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Tempo Total</div>
            </div>
            <div className="bg-black/30 border border-purple-500/20 rounded-2xl p-5 text-center transition-all hover:border-purple-500/40 hover:-translate-y-[5px]">
              <div className="text-3xl mb-2.5">📅</div>
              <div className="text-[28px] font-bold text-purple-600 mb-1">{formatTempo(user.weekly_seconds || 0)}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Tempo Semanal</div>
            </div>
            <div className="bg-black/30 border border-purple-500/20 rounded-2xl p-5 text-center transition-all hover:border-purple-500/40 hover:-translate-y-[5px]">
              <div className="text-3xl mb-2.5">📊</div>
              <div className="text-[28px] font-bold text-purple-600 mb-1">{formatTempo(user.monthly_seconds || 0)}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Tempo Mensal</div>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          {/* Weekly Progress */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md animate-[slideIn_0.6s_ease-out]">
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-semibold flex items-center gap-2.5">📈 Progresso Semanal</div>
              <div className="text-xl font-bold text-purple-600">{weeklyPercentage.toFixed(1)}%</div>
            </div>
            <div className="flex justify-between mb-3 text-xs">
              <span className="text-gray-400">Tempo Semanal</span>
              <span className="text-white font-semibold">{formatTempo(user.weekly_seconds || 0)}</span>
            </div>
            <div className="flex justify-between mb-3 text-xs">
              <span className="text-gray-400">Tempo Total</span>
              <span className="text-white font-semibold">{formatTempo(user.total_seconds || 0)}</span>
            </div>
            <div className="bg-black/30 h-3 rounded-[10px] overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 rounded-[10px] transition-all duration-1000 ease-out relative"
                style={{ width: `${weeklyPercentage}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_infinite]"></div>
              </div>
            </div>
          </div>

          {/* Monthly Progress */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md animate-[slideIn_0.6s_ease-out]">
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-semibold flex items-center gap-2.5">📊 Progresso Mensal</div>
              <div className="text-xl font-bold text-purple-600">{monthlyPercentage.toFixed(1)}%</div>
            </div>
            <div className="flex justify-between mb-3 text-xs">
              <span className="text-gray-400">Tempo Mensal</span>
              <span className="text-white font-semibold">{formatTempo(user.monthly_seconds || 0)}</span>
            </div>
            <div className="flex justify-between mb-3 text-xs">
              <span className="text-gray-400">Tempo Total</span>
              <span className="text-white font-semibold">{formatTempo(user.total_seconds || 0)}</span>
            </div>
            <div className="bg-black/30 h-3 rounded-[10px] overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 rounded-[10px] transition-all duration-1000 ease-out relative"
                style={{ width: `${monthlyPercentage}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_infinite]"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Mural da Vergonha Section */}
        {isOwnProfile && (
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 mb-6 backdrop-blur-md animate-[slideIn_0.5s_ease-out]">
            <div className="flex justify-between items-center mb-5 pb-4 border-b-2 border-purple-500/20">
              <div className="flex items-center gap-3 text-lg font-semibold">
                <span className="text-2xl animate-[float_3s_ease-in-out_infinite]">😱</span>
                <span>Mural da Vergonha</span>
              </div>
              <button 
                onClick={() => {
                  setVideoModalType('shame-wall');
                  setShowVideoModal(true);
                }}
                className="bg-purple-600/15 border border-purple-600/30 px-5 py-2 rounded-[10px] text-purple-600 cursor-pointer transition-all text-xs font-medium hover:bg-purple-600/25 hover:-translate-y-[2px]"
              >
                + Adicionar Vídeo
              </button>
            </div>
            {videosLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : (
              <VideoCarousel 
                videos={shameWallVideos} 
                videoType="shame-wall"
                onVideoClick={handleVideoClick}
                onAddVideo={() => {
                  setVideoModalType('shame-wall');
                  setShowVideoModal(true);
                }}
                isOwnProfile={isOwnProfile}
              />
            )}
          </div>
        )}

        {/* Melhores Clipes Section */}
        {isOwnProfile && (
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 mb-6 backdrop-blur-md animate-[slideIn_0.5s_ease-out]">
            <div className="flex justify-between items-center mb-5 pb-4 border-b-2 border-purple-500/20">
              <div className="flex items-center gap-3 text-lg font-semibold">
                <span className="text-2xl animate-[float_3s_ease-in-out_infinite]">⭐</span>
                <span>Melhores Clipes</span>
              </div>
              <button 
                onClick={() => {
                  setVideoModalType('best-clips');
                  setShowVideoModal(true);
                }}
                className="bg-purple-600/15 border border-purple-600/30 px-5 py-2 rounded-[10px] text-purple-600 cursor-pointer transition-all text-xs font-medium hover:bg-purple-600/25 hover:-translate-y-[2px]"
              >
                + Adicionar Vídeo
              </button>
            </div>
            {videosLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : (
              <VideoCarousel 
                videos={bestClipsVideos} 
                videoType="best-clips"
                onVideoClick={handleVideoClick}
                onAddVideo={() => {
                  setVideoModalType('best-clips');
                  setShowVideoModal(true);
                }}
                isOwnProfile={isOwnProfile}
              />
            )}
          </div>
        )}

        {/* Publications Section */}
        <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-8 backdrop-blur-md animate-[slideIn_0.7s_ease-out]">
          <div className="flex justify-between items-center mb-8">
            <div className="text-lg font-semibold flex items-center gap-3">
              📚 {isOwnProfile ? 'Minhas Publicações' : 'Publicações'}
            </div>
            {isOwnProfile && (
              <button
                onClick={() => setShowPostModal(true)}
                className="bg-gradient-to-br from-purple-600 to-indigo-600 border-none px-8 py-3 rounded-xl text-white font-semibold cursor-pointer transition-all text-sm shadow-[0_4px_15px_rgba(139,92,246,0.4)] hover:-translate-y-[2px] hover:shadow-[0_8px_25px_rgba(139,92,246,0.6)]"
              >
                + Nova Publicação
              </button>
            )}
          </div>

          {/* Lista de Posts */}
          {postsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-400">Carregando publicações...</p>
            </div>
          ) : userPosts.length === 0 ? (
            <div className="text-center py-8">
              <Film className="mx-auto mb-4 text-gray-500" size={48} />
              <p className="text-gray-400 mb-4">
                {isOwnProfile ? 'Você ainda não publicou nenhuma avaliação.' : 'Este usuário ainda não publicou nenhuma avaliação.'}
              </p>
              {isOwnProfile && (
                <button
                  onClick={() => setShowPostModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl font-bold hover:shadow-[0_0_20px_rgba(139,92,246,0.5)] transition-all"
                >
                  Criar Primeira Publicação
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              {userPosts.map((post) => {
                const postLiked = post.is_liked || false;
                const isLiking = likingPosts.has(post.id);
                const authorAvatar = post.author_avatar 
                  ? getDiscordAvatarUrl(post.author_discord_id, post.author_avatar)
                  : getDiscordAvatarUrl(post.author_discord_id || '0', null);
                
                return (
                  <div 
                    key={post.id} 
                    className="bg-black/30 border border-purple-500/15 rounded-2xl p-5 transition-all hover:border-purple-500/40 hover:-translate-y-[3px] hover:shadow-[0_8px_30px_rgba(139,92,246,0.2)] cursor-pointer"
                    onClick={() => navigate(`/post/${post.id}`)}
                  >
                    
                    {/* Publication Header */}
                    <div className="flex gap-4 mb-4">
                      {post.movie_poster && (
                        <img 
                          src={post.movie_poster} 
                          alt={post.movie_title}
                          className="w-20 h-[120px] rounded-[10px] object-cover border-2 border-purple-600/30 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-2">{post.movie_title || 'Filme'}</h3>
                        {post.rating && (
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="text-yellow-400 text-base">
                              {'⭐'.repeat(Math.floor(post.rating))}
                            </span>
                            <span className="text-purple-600 font-semibold text-base">{parseFloat(post.rating).toFixed(1)}</span>
                          </div>
                        )}
                        <div className="flex gap-4 text-xs text-gray-500 mb-3">
                          <span>{post.movie_data?.genres?.[0]?.name || 'Filme'}</span>
                          <span>•</span>
                          <span>{formatTimeAgo(post.created_at)}</span>
                        </div>
                        {post.movie_data?.genres && post.movie_data.genres.length > 0 && (
                          <div className="flex gap-2 flex-wrap">
                            {post.movie_data.genres.slice(0, 3).map((genre, idx) => (
                              <span key={idx} className="px-3 py-1 bg-purple-600/15 border border-purple-600/30 rounded-[20px] text-[11px] text-purple-400">
                                {genre.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Publication Actions */}
                    <div 
                      className="flex gap-5 pt-4 border-t border-purple-500/10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikePost(post.id);
                        }}
                        disabled={isLiking}
                        className="flex items-center gap-1.5 text-xs hover:text-red-400 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed px-2.5 py-1.5 rounded-lg hover:bg-purple-600/10"
                      >
                        <Heart 
                          size={16} 
                          className={`${postLiked ? 'fill-red-500 text-red-500' : 'text-gray-400 group-hover:text-red-400'} transition-colors`}
                        />
                        <span className={postLiked ? 'text-red-500' : 'text-gray-400'}>
                          {post.likes || 0} curtidas
                        </span>
                      </button>
                      
                      <button className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-purple-400 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-purple-600/10">
                        <MessageCircle size={16} />
                        <span>{post.comments_count || 0} comentários</span>
                      </button>
                      
                      <button className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-pink-400 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-purple-600/10">
                        <Share2 size={16} />
                        <span>Compartilhar</span>
                      </button>
                      
                      {isOwnProfile && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePost(post.id);
                          }}
                          className="ml-auto text-gray-400 hover:text-red-400 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-red-500/10"
                          title="Excluir publicação"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Publicar Avaliação */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/20 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-black flex items-center gap-2">
                <Film className="text-purple-600" size={24} />
                Publicar Avaliação de Filme
              </h2>
              <button
                onClick={() => {
                  setShowPostModal(false);
                  handleClearForm();
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitPost} className="p-6 space-y-6">
              {/* Busca de Filme */}
              <div>
                <label className="block text-sm font-bold mb-2">Filme *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      handleSearchMovies(e.target.value);
                    }}
                    placeholder="Buscar filme..."
                    className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 outline-none focus:border-purple-500/50 transition-colors text-white"
                  />
                </div>
                
                {/* Resultados da busca */}
                {searchResults.length > 0 && !selectedMovie && (
                  <div className="mt-2 border border-white/10 rounded-sm bg-black/60 max-h-60 overflow-y-auto">
                    {searchResults.map((movie) => (
                      <div
                        key={movie.id}
                        onClick={() => handleSelectMovie(movie)}
                        className="p-3 hover:bg-white/10 cursor-pointer flex items-center gap-3 border-b border-white/5 last:border-0"
                      >
                        {movie.poster_path && (
                          <img
                            src={movie.poster_path.startsWith('http') ? movie.poster_path : `https://image.tmdb.org/t/p/w92${movie.poster_path}`}
                            alt={movie.title}
                            className="w-12 h-18 object-cover rounded-sm"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-bold">{movie.title}</p>
                          <p className="text-xs text-gray-400">{movie.release_date ? new Date(movie.release_date).getFullYear() : movie.year}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Filme selecionado */}
                {selectedMovie && (
                  <div className="mt-3 p-4 bg-white/5 border border-purple-500/50 rounded-sm flex items-center gap-4">
                    {selectedMovie.poster_path && (
                      <img
                        src={selectedMovie.poster_path.startsWith('http') ? selectedMovie.poster_path : `https://image.tmdb.org/t/p/w154${selectedMovie.poster_path}`}
                        alt={selectedMovie.title}
                        className="w-20 h-30 object-cover rounded-sm"
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-bold text-lg">{selectedMovie.title}</p>
                      <p className="text-sm text-gray-400">{selectedMovie.release_date ? new Date(selectedMovie.release_date).getFullYear() : selectedMovie.year}</p>
                      {selectedMovie.vote_average && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="text-yellow-400 fill-yellow-400" size={14} />
                          <span className="text-sm text-yellow-400">{selectedMovie.vote_average.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMovie(null);
                        setSearchQuery('');
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-bold mb-2">Sua Nota (0-10)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={rating}
                    onChange={(e) => setRating(parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-xl font-bold text-yellow-400 w-16 text-right">
                    {rating > 0 ? rating.toFixed(1) : '-'}
                  </span>
                </div>
              </div>

              {/* Review */}
              <div>
                <label className="block text-sm font-bold mb-2">Avaliação *</label>
                <textarea
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  placeholder="Compartilhe seus pensamentos sobre o filme..."
                  rows={5}
                  className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 outline-none focus:border-purple-500/50 transition-colors resize-none text-white"
                  required
                />
              </div>

              {/* Attendees */}
              <div>
                <label className="block text-sm font-bold mb-2">Quem assistiu com você?</label>
                <UserSearchInput
                  users={attendees}
                  onAddUser={handleAddAttendee}
                  onRemoveUser={handleRemoveAttendee}
                  placeholder="Buscar por nome ou Discord ID..."
                />
                
                {attendees.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {attendees.map((attendee, idx) => {
                      const attendeeDiscordId = typeof attendee === 'string' ? attendee : attendee.discord_id;
                      const username = typeof attendee === 'string' ? attendee : (attendee.username || attendeeDiscordId);
                      const avatar = typeof attendee === 'string' ? null : attendee.avatar;
                      
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-3 py-1 bg-purple-600/20 border border-purple-600/50 rounded-sm text-sm"
                        >
                          {avatar && (
                            <img
                              src={getDiscordAvatarUrl(attendeeDiscordId, avatar)}
                              alt={username}
                              className="w-5 h-5 rounded-full"
                            />
                          )}
                          <span>{username}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveAttendee(attendee)}
                            className="text-gray-400 hover:text-white"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setShowPostModal(false);
                    handleClearForm();
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm font-bold transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || !selectedMovie || !review.trim()}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-sm font-bold hover:shadow-[0_0_20px_rgba(139,92,246,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Publicando...' : 'Publicar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Adicionar Vídeo */}
      <AddVideoModal 
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoType={videoModalType}
        onVideoAdded={handleVideoAdded}
      />

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
          50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
    
    {/* Modal para criar story */}
    {isOwnProfile && (
      <CreateStoryModal
        isOpen={showCreateStoryModal}
        onClose={() => setShowCreateStoryModal(false)}
        onStoryCreated={() => {
          setShowCreateStoryModal(false);
        }}
      />
    )}
    </>
  );
};

export default Perfil;
